<template>
  <q-page>
    <div class="justify-center row">
      <div v-if="!$apollo.loading" class="col-10 q-pa-md">
        <h2 class="q-mt-sm q-mb-sm text-center">Retail Order {{retailOrder.orderNumber}}</h2>
        <div class="text-h4 text-center">({{retailOrder.name}})</div>
        <h3>Details</h3>
        <div class="col q-gutter-md">
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Placed:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.placed) || 'Not placed'}}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Verified:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.verified) || 'Not verified'}}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Paid:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.paid) || 'Not paid'}}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Packed:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.packed) || 'Not packed'}}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Shipped:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.shipped) || 'Not shipped'}}
            </div>
          </div>
          <div class="row items-center">
            <div style="width: 8em; text-align: right;">Delivered:</div>
            <div class="col-auto q-pl-md">
              {{formatDateTime(retailOrder.delivered) || 'Not delivered'}}
            </div>
          </div>
          <div class="row">
            <div class="col-1 text-right">Ship to:</div>
            <div class="col-3 q-pl-md">
              <view-shipping-details :value="retailOrder.shipping.address"/>
            </div>
          </div>
        </div>
        <h3 class="q-mb-none">Notes</h3>
        <div class="row q-pa-sm" style="white-space: pre-wrap;">{{retailOrder.notes}}</div>
        <h3>Order</h3>
        <q-table
          :data="retailOrder.items"
          :columns="orderColumns"
          :pagination="{rowsPerPage: 0}"
          binary-state-sort
          row-key="name" >

          <template v-slot:body-cell-img="props">
            <q-td class="text-center">
              <q-img dense :src="props.value + '.png.thumb'" style="width: 25px; height: 25px;" contain>
                <q-tooltip anchor="center right" self="center left" :offset="[10, 10]"
                           transition-show="scale"
                           transition-hide="scale"
                           content-style="background: white; border: solid 1px black">
                  <q-img dense :src="props.value" style="width: 25vw; height: 25vw;" contain/>
                </q-tooltip>
              </q-img>
            </q-td>
          </template>
          <template v-slot:body-cell-colour="props">
            <q-td :props="props">
              <div :style="{border: '1px silver solid', backgroundColor: props.row.sku.options.colour.hex, width: '10px',height: '10px',marginRight:'5px'}" class="inline-block"></div>
              {{props.row.sku.options.colour.group + ' (' + props.row.sku.options.colour.name + ')'}}
            </q-td>
          </template>
        </q-table>

        <div class="text-h5 q-pt-md text-right">
          Items: {{ formatCurrency(retailOrder.totalCostOfItems) }}
        </div>
        <div class="text-h5 q-pt-md text-right" v-if="retailOrder.voucherDiscountAmount">
          Voucher Discount: <span style="color: darkred;">{{ formatCurrency(retailOrder.voucherDiscountAmount) }}</span>
        </div>
        <div class="text-h5 q-pt-md text-right">
          Shipping and Handling: {{ formatCurrency(retailOrder.shipping.cost) }}
        </div>
        <div class="text-h4 q-pt-md text-right">
          Total Cost: {{ formatCurrency(retailOrder.totalCost) }}
        </div>
        <div class="text-h5 text-right text-grey">
          Includes GST: {{ formatCurrency(retailOrder.totalCost*(1-1/1.1)) }}
        </div>

        <h3 class="q-mt-none q-mb-md">Break Downs</h3>
        <div class="row">
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Brand"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Type"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Material"
              :items="retailOrder.items"
            />
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
            <summary-table
              title="Colour"
              :items="retailOrder.items"
              :group-field-callback="row => (((row.sku||{}).options||{}).colour||{}).group">
              <template v-slot:group="props">
                <div :style="{border: '1px silver solid', backgroundColor: (((props.row.item.sku||{}).options||{}).colour||{}).hex, width: '10px',height: '10px'}" class="inline-block">
                </div>
                {{props.row.group}}
              </template>
            </summary-table>
          </div>
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Screws You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="retailOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('screws', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('screws', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>          
          <div class="col-12 col-sm-12 col-md-6 col-lg-4 col-xl-3 q-pa-sm">
          <summary-table
            title="Bolts You'll Need"
            sumName="Qty"
            groupName="Length"
            :items="retailOrder.items"
            :group-field-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.type)"
            :sum-callback="row => getProductAttributeFromRow('bolts', row).map(x => x.qty * row.qty)"
            :hide-price-column="true">
          </summary-table>
          </div>    
        </div>
      </div>
    </div>
  </q-page>
</template>

<style>
</style>

<script>
import gql from 'graphql-tag'
import { colourToString, formatCurrency, formatDateTime } from '../../util/string'
import SummaryTable from '../../components/summaryTable'
import ViewShippingDetails from '../../components/ui/ViewShippingDetails'
import getNestedFieldInObject from '@/util/getNestedFieldInObject'

const retailOrderFragment = gql`
  fragment RetailOrderFragment on RetailOrder {
    id
    orderNumber
    name
    notes
    createdAt
    totalCost
    totalCostOfItems
    voucherDiscountAmount
    shipping {
      cost
      address {
        contactName
        contactLastname
        address
        address2
        city
        state
        postCode
        country
        phoneNumber
      }
    }
    account {
      id
    }
    placed
    verified
    paid
    packed
    shipped
    delivered
    items {
      id
      skuId
      sku {
          id
          options
          retailPrice
          product {
              id
              name
              attributes
              images {
                url
                name
              }
              urlSlug
          }
      }
      qty
      notes
      itemCost
      discountPercent
      totalCost
    }
  }`

export default {
  name: 'PageAccountRetailOrder',
  components: { ViewShippingDetails, SummaryTable },
  apollo: {
    retailOrder () {
      return {
        query: gql`
          query retailOrder ($id: ID!){
            retailOrder (id: $id) {
              ...RetailOrderFragment
            }
          }
          ${retailOrderFragment}
        `,
        variables: {
          id: this.id
        },
        fetchPolicy: 'network-only'
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    orderColumns () {
      return [
        { name: 'img', field: row => row.sku?.product?.images?.[0]?.url },
        { name: 'brand', label: 'Brand', field: row => row.sku?.product?.attributes?.brand, sortable: true, align: 'left' },
        { name: 'range', label: 'Range', field: row => row.sku?.product?.attributes?.range, sortable: true, align: 'left' },
        { name: 'name', label: 'Name', field: row => row.sku?.product?.name || 'Not Found', sortable: true, align: 'left' },
        // { name: 'size', label: 'Size', field: row => row.sku?.product?.attributes?.size, sortable: true },
        // { name: 'grip', label: 'Grip', field: row => row.sku?.product?.attributes?.grip, sortable: true },
        { name: 'colour', label: 'Colour', field: row => colourToString(row.sku?.options?.colour), sortable: true, align: 'left' },
        { name: 'holds', label: 'Holds', field: row => Number(row.sku?.product?.attributes?.holds), sortable: true, align: 'left' },
        { name: 'price', label: 'Price', field: row => Number(row.itemCost), sortable: true, format: formatCurrency },
        { name: 'qty', label: 'Qty', field: row => Number(row.qty), sortable: true },
        ...this.createDiscountColumn(),
        { name: 'total', label: 'Total', field: row => Number(row.totalCost), sortable: true, format: formatCurrency }
      ]
    }
  },
  methods: {
    getNestedFieldInObject: getNestedFieldInObject,
    getProductAttributeFromRow (attributeName, row) {
      return this.getNestedFieldInObject(`sku.product.attributes.${attributeName}`, row) || []
    },    
    formatDateTime: formatDateTime,
    formatCurrency: formatCurrency,
    orderHasDiscount () {
      return this.retailOrder.items?.some?.(item => item.discountPercent)
    },
    createDiscountColumn () {
      if (!this.orderHasDiscount()) return []
      return [{ name: 'discount', label: 'Discount', field: 'discountPercent', sortable: true, format: v => `${Number(v).toFixed(1)}%` }]
    }
  },
  data () {
    return {
      retailOrder: {},
      summaryColumns: [
        { name: 'brand', required: true, label: 'Brand', align: 'left', field: row => row.group, sortable: true },
        { name: 'holds', required: true, label: 'Holds', align: 'left', field: row => row.value, sortable: true }
      ]
    }
  }
}
</script>
